import React from 'react'
import { BrushCleaning } from 'lucide-react'

import { nutrimaticDevice, nutrimaticLabels } from '@/nutrimatic'
import { cn } from '@/lib/utils'

export const NUTRIMATIC_BALANCE_FRACTIONS = [0.25, 0.5, 0.75, 1] as const

function formatPresetLabel(fraction: number) {
  return fraction === 1 ? nutrimaticLabels.mobileSimple.maxPreset : `${Math.round(fraction * 100)}%`
}

export function NutrimaticBalancePresetStrip({
  fractions,
  activeFraction,
  onSelect,
  disabled,
  disabledFractions = [],
  onClear,
  clearDisabled,
  showSafeMax,
  onSafeMax,
  safeMaxDisabled,
  safeMaxActive,
  variant = 'nutrimatic',
}: {
  fractions: readonly number[]
  activeFraction: number | null
  onSelect: (fraction: number) => void
  disabled?: boolean
  disabledFractions?: readonly number[]
  onClear?: () => void
  clearDisabled?: boolean
  showSafeMax?: boolean
  onSafeMax?: () => void
  safeMaxDisabled?: boolean
  safeMaxActive?: boolean
  variant?: 'nutrimatic' | 'mobile'
}) {
  const isMobile = variant === 'mobile'
  const disabledFractionSet = new Set(disabledFractions)

  const renderFractionButtons = () =>
    fractions.map((fraction) => {
      const isActive = activeFraction === fraction && !safeMaxActive
      const fractionDisabled = disabled || disabledFractionSet.has(fraction)
      const label = formatPresetLabel(fraction)

      if (isMobile) {
        return (
          <button
            key={fraction}
            type="button"
            className={cn('nm-mobile-simple__preset', isActive && 'nm-mobile-simple__preset--active')}
            disabled={fractionDisabled}
            onClick={() => onSelect(fraction)}
            aria-pressed={isActive}
            aria-label={`Usar ${label} del saldo`}
          >
            {label}
          </button>
        )
      }

      return (
        <button
          key={fraction}
          type="button"
          disabled={fractionDisabled}
          onClick={() => onSelect(fraction)}
          className={cn(
            nutrimaticDevice.physicalSelectorBtn,
            isActive && nutrimaticDevice.physicalSelectorBtnActive,
          )}
          aria-pressed={isActive}
          aria-label={`Usar ${label} del saldo`}
        >
          {label}
        </button>
      )
    })

  const renderSafeMaxButton = () => {
    if (!showSafeMax || !onSafeMax) return null
    const label = nutrimaticLabels.mobileSimple.safeMaxPreset

    if (isMobile) {
      return (
        <button
          type="button"
          className={cn(
            'nm-mobile-simple__preset nm-mobile-simple__preset--safe-max',
            safeMaxActive && 'nm-mobile-simple__preset--active',
          )}
          disabled={safeMaxDisabled}
          onClick={onSafeMax}
          aria-pressed={safeMaxActive}
          aria-label={label}
        >
          {label}
        </button>
      )
    }

    return (
      <button
        type="button"
        disabled={safeMaxDisabled}
        onClick={onSafeMax}
        className={cn(
          nutrimaticDevice.physicalSelectorBtn,
          'nutrimatic-physical-selector__btn--safe-max',
          safeMaxActive && nutrimaticDevice.physicalSelectorBtnActive,
        )}
        aria-pressed={safeMaxActive}
        aria-label={label}
      >
        {label}
      </button>
    )
  }

  const renderClearButton = () => {
    if (!onClear) return null

    if (isMobile) {
      return (
        <button
          type="button"
          className="nm-mobile-simple__preset nm-mobile-simple__preset--icon nm-mobile-simple__preset--clear"
          disabled={clearDisabled}
          onClick={onClear}
          aria-label={nutrimaticLabels.mobileSimple.clearAmounts}
        >
          <BrushCleaning className="size-4" aria-hidden />
        </button>
      )
    }

    return (
      <button
        type="button"
        disabled={clearDisabled}
        onClick={onClear}
        className={cn(
          nutrimaticDevice.physicalSelectorBtn,
          'nutrimatic-physical-selector__btn--icon nutrimatic-physical-selector__btn--aux',
        )}
        aria-label={nutrimaticLabels.mobileSimple.clearAmounts}
      >
        <BrushCleaning className="size-3.5" aria-hidden />
      </button>
    )
  }

  if (isMobile) {
    return (
      <div className="nm-mobile-simple__presets">
        {renderFractionButtons()}
        {renderSafeMaxButton()}
        {renderClearButton()}
      </div>
    )
  }

  return (
    <div className={nutrimaticDevice.displayPresets}>
      <div
        className={nutrimaticDevice.physicalSelector}
        role="group"
        aria-label="Fracciones del saldo disponible"
      >
        {renderFractionButtons()}
        {renderSafeMaxButton()}
        {renderClearButton()}
      </div>
    </div>
  )
}
